<script setup lang="ts">
import { defineAsyncComponent } from '@@/bits/vue'
import type { QueuedGlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { trackSnackbarEvent, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { storeToRefs } from 'pinia'

const OzBaseSnackbar = defineAsyncComponent(() => import('@@/library/v4/components/OzBaseSnackbar.vue'))

const { darkMode = 'auto', topOffset = 0 } = defineProps<{
  darkMode?: boolean | 'auto'
  topOffset?: number
}>()

const globalSnackbarStore = useGlobalSnackbarStore()
const { snackbarArray } = storeToRefs(globalSnackbarStore)
const { executeActions, removeSnackbar } = globalSnackbarStore

function onClick(snackbar: QueuedGlobalSnackbarNotification): void {
  if (snackbar.dismissable !== true) {
    return
  }
  trackSnackbarEvent('Dismissed', snackbar)
  removeSnackbar(snackbar.uid)
}
</script>

<template>
  <!-- 33px is the height of the first snackbar including margin (49px)
   minus the initial gap (16px) from the top of viewport -->
  <div
    :class="['fixed w-full flex flex-col-reverse items-center pointer-events-none start-0 isolate z-global-snackbar']"
    :style="{ top: `${33 + topOffset}px` }"
    role="status"
  >
    <transition-group name="snackbar-transition" tag="div" class="contents">
      <OzBaseSnackbar
        v-for="(snackbar, i) in snackbarArray"
        :key="snackbar.uid"
        :data-testid="`snackbar${i}`"
        :dark-mode="darkMode"
        class="overflow-visible transition-[top,opacity]"
        :notification-type="snackbar.notificationType"
        :message="snackbar.message"
        :icon-name="snackbar.iconName"
        :uid="snackbar.uid"
        :action-text="snackbar.actionText"
        :should-truncate="snackbar.shouldTruncate"
        :loading-state="snackbar.loadingState"
        @action-text-click="executeActions(snackbar.actionTextActions, $event, snackbar.uid)"
        @click="onClick(snackbar)"
      >
        <template v-if="snackbar.actionContentSlot" #action-content>
          <component
            :is="snackbar.actionContentSlot"
            v-bind="snackbar.actionContentSlotProps || {}"
            :dark-mode="darkMode"
            :notification-type="snackbar.notificationType"
          />
        </template>
      </OzBaseSnackbar>
    </transition-group>
  </div>
</template>

<style lang="scss" scoped>
// Animation functions follows https://material.io/design/motion/speed.html#easing
.snackbar-transition-enter-active {
  /* Ease in for entering */
  animation: fadeInTop 0.3s cubic-bezier(0, 0, 0.2, 1);
}
.snackbar-transition-leave-active,
.snackbar-transition-leave-to {
  /* Ease out for leaving */
  animation: fadeOutTop 0.3s cubic-bezier(0.4, 0, 1, 1);
}

@keyframes fadeInTop {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadeOutTop {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}
</style>
